"use client";

import Link from "next/link";
import { ArrowRight, Github, Instagram, Linkedin, Twitter, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";
import { ThemeText } from "@/components/ui/theme-utils";

interface FooterProps {
  isHomePage?: boolean;
}

export function Footer({ isHomePage = true }: FooterProps) {
  const { isDark } = useTheme();
  const currentYear = new Date().getFullYear();

  const featuresLink = isHomePage ? "#features" : "/#features";
  const howLink = isHomePage ? "#how-it-works" : "/#how-it-works";
  const securityLink = isHomePage ? "#security" : "/#security";

  return (
    <footer
      className={cn(
        "relative pt-20 sm:pt-24 pb-10 border-t overflow-hidden",
        isDark
          ? "bg-[#07070B] border-white/10"
          : "bg-[#FAFAF9] border-stone-200/80"
      )}
    >
      {/* Subtle gradient glow at top */}
      <div
        className="pointer-events-none absolute inset-x-0 -top-40 h-80 opacity-30 blur-3xl"
        style={{
          background:
            "radial-gradient(circle, rgba(0,0,0,0.35), transparent 70%)"
        }}
      />

      <div className="container mx-auto px-5 sm:px-6 relative">
        {/* Top: newsletter + brand */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 pb-14 border-b border-stone-200/60 dark:border-white/5">
          <div className="lg:col-span-6">
            <Link href="/" className="inline-flex items-center gap-2.5">
              <img
                src="/images/zset-logo.png"
                alt="Zset"
                width={36}
                height={36}
                className="h-9 w-9 rounded-full dark:invert"
              />
              <span className="text-xl font-semibold tracking-tight">Zset</span>
            </Link>
            <p
              className={cn(
                "mt-5 text-base sm:text-lg max-w-md",
                isDark ? "text-white/70" : "text-stone-600"
              )}
            >
              Store, share, and collaborate on the largest files your team works
              with. Built for the speed of modern production.
            </p>

            {/* Newsletter */}
            <form
              className={cn(
                "mt-7 flex items-center gap-2 max-w-md rounded-full p-1.5",
                isDark
                  ? "bg-white/[0.04] border border-white/10"
                  : "bg-white border border-stone-200"
              )}
              onSubmit={(e) => e.preventDefault()}
            >
              <input
                type="email"
                placeholder="you@company.com"
                className={cn(
                  "flex-1 bg-transparent px-4 py-2 text-sm focus:outline-none",
                  isDark ? "placeholder:text-white/40" : "placeholder:text-stone-400"
                )}
              />
              <button
                type="submit"
                className="inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-brand-700 to-brand-900 shadow-glow-sm"
              >
                Subscribe
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </form>
            <p className="mt-2 text-xs text-stone-500">
              Monthly product updates only. No spam.
            </p>
          </div>

          <div className="lg:col-span-6 grid grid-cols-2 sm:grid-cols-3 gap-10">
            <FooterCol
              title="Product"
              links={[
                { href: featuresLink, label: "Features" },
                { href: howLink, label: "How it works" },
                { href: securityLink, label: "Security" },
                { href: "/downloads", label: "Download" }
              ]}
            />
            <FooterCol
              title="Company"
              links={[
                { href: "/contact", label: "Contact" },
                { href: "/#testimonials", label: "Customers" },
                { href: "/#faq", label: "FAQ" }
              ]}
            />
            <FooterCol
              title="Legal"
              links={[
                { href: "/terms", label: "Terms" },
                { href: "/privacy-policy", label: "Privacy" },
                { href: "/refund-policy", label: "Refunds" }
              ]}
            />
          </div>
        </div>

        {/* Bottom: status + meta */}
        <div className="mt-10 flex flex-col-reverse md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-stone-500">
            <span>© {currentYear} Zset</span>
            <span className="hidden sm:inline">·</span>
            <span className="inline-flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              All systems operational
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            <SocialLink href="https://www.instagram.com/zset.in/" label="Instagram">
              <Instagram className="h-4 w-4" />
            </SocialLink>
            <SocialLink href="#" label="Twitter">
              <Twitter className="h-4 w-4" />
            </SocialLink>
            <SocialLink href="#" label="LinkedIn">
              <Linkedin className="h-4 w-4" />
            </SocialLink>
            <SocialLink href="#" label="GitHub">
              <Github className="h-4 w-4" />
            </SocialLink>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({
  title,
  links
}: {
  title: string;
  links: { href: string; label: string }[];
}) {
  const { isDark } = useTheme();
  return (
    <div>
      <h3 className="text-sm font-semibold mb-4 tracking-tight">{title}</h3>
      <ul className="space-y-2.5">
        {links.map((l) => (
          <li key={l.label}>
            <Link
              href={l.href}
              className={cn(
                "text-sm transition-colors",
                isDark
                  ? "text-white/60 hover:text-white"
                  : "text-stone-600 hover:text-stone-900"
              )}
            >
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SocialLink({
  href,
  label,
  children
}: {
  href: string;
  label: string;
  children: React.ReactNode;
}) {
  const { isDark } = useTheme();
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className={cn(
        "inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors",
        isDark
          ? "bg-white/[0.04] text-white/60 hover:text-white hover:bg-white/[0.08]"
          : "bg-stone-100 text-stone-600 hover:text-stone-900 hover:bg-stone-200"
      )}
    >
      {children}
    </a>
  );
}
